import { useState, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, Text, Button, IconButton, Dropdown, Avatar, Tag, notification, Pagination, EditableText, space, iconWeight, Icon, tableActionsCell } from '@nula/core-ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNavigateToEntity } from '@/hooks/navigation';
import { Library } from './library';

import type { WorkspaceDoc } from '@/lib/sync/entity-types';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { formatDate } from '@/utils/format-date';
import { shortcutLabel } from '@/lib/platform/platform';
import { isSharedWithMe, canRemoveSelf, canEdit, canDelete } from '@/lib/sharing/utils/permissions';
import { deriveOwner } from '@/lib/sharing/utils/derive-owner';
import { ConfirmMenuItem } from '@/components/confirm-menu-item/confirm-menu-item';
import { type AccessFilter } from './library';
import { DocumentCard } from './document-card';
import { displayTitle } from '@/lib/documents/doc-title';
import { MiniPageCard } from './mini-page-card';
import { BulkAddToCollectionPicker } from './bulk-add-to-collection-picker';
import { SelectionActions } from './selection-actions';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './document-library.module.css';
import tableStyles from './library-table.module.css';

// ── Constants ────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { key: 'All', label: 'All', icon: <Icon name="apps" size={14} weight={iconWeight.light} /> },
  { key: 'Documents', label: 'Documents', icon: <Icon name={getEntityKindMeta('document').icon} size={14} weight={iconWeight.light} /> },
  { key: 'Canvases', label: 'Canvases', icon: <Icon name={getEntityKindMeta('canvas').icon} size={14} weight={iconWeight.light} /> },
];
const PAGE_SIZE = 12;

// ── Table title cell with inline rename ───────────────────────────────────────

function DocTableTitleCell({
  doc,
  renaming,
  onOpen: _onOpen,
  onRename,
  onRenameEnd,
  isSelected,
  isSelecting,
  onToggleSelect,
}: {
  doc: WorkspaceDoc;
  renaming: boolean;
  onOpen: (id: string) => void;
  onRename?: (id: string, title: string) => void;
  onRenameEnd: () => void;
  isSelected?: boolean;
  isSelecting?: boolean;
  onToggleSelect?: () => void;
}) {
  return (
    <Flex align="center" gap={space[3]} className={tableStyles.nameCell}>
      <MiniPageCard
        previewMarkup={doc.previewMarkup}
        previewKind={doc.previewKind}
        kind={doc.kind}
        isSelected={isSelected}
        isSelecting={isSelecting}
        onToggleSelect={onToggleSelect}
      />
      <Flex vertical className={tableStyles.titleCol}>
        <EditableText
          value={displayTitle(doc)}
          onChange={(val) => { if (val.trim() && val.trim() !== doc.title) onRename?.(doc.id, val.trim()); }}
          editing={renaming}
          onEditingChange={(v) => { if (!v) onRenameEnd(); }}
          size="sm"
          renderDisplay={(val) => <Text ellipsis className={tableStyles.titleText}>{val}</Text>}
        />
        {doc.tags.length > 0 && (
          <Flex align="center" wrap gap={space[1]} className={tableStyles.tagsRow}>
            {doc.tags.slice(0, 3).map((tag) => (
              <Tag key={tag} bordered={false} className={tableStyles.tag}>{tag}</Tag>
            ))}
            {doc.tags.length > 3 && <Text size="xs" className={tableStyles.tagsOverflow}>+{doc.tags.length - 3}</Text>}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

// ── Main component ──────────────────────────────────────────────────────────


export function DocumentLibrary() {
  const user = useAuthStore((s) => s.user);
  const service = useWorkspaceService();
  const allDocs = useWorkspaceDocs();
  // Only unarchived docs in the library
  const allDocuments = useMemo(() => allDocs.filter((d) => !d.isArchived), [allDocs]);

  const navigateToEntity = useNavigateToEntity();

  const createNewDocument = async (type: 'document' | 'canvas' = 'document') => {
    const { id } = await service.createDoc({ kind: type });
    navigateToEntity(id);
  };

  const [activeType, setActiveType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('modified');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);

  // ── Access-filter buckets ────────────────────────────────────────────────
  const sharedDocs = useMemo(() => allDocuments.filter((d) => isSharedWithMe(d)), [allDocuments]);

  const handleAccessFilterChange = (filter: AccessFilter) => {
    setAccessFilter(filter);
    setPage(1);
  };

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    allDocuments.forEach((d) => d.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [allDocuments]);

  const filteredItems = useMemo(() => {
    // Apply access filter first
    let items = accessFilter === 'shared' ? sharedDocs : allDocuments;
    if (activeType === 'Documents') {
      items = items.filter((d) => d.kind === 'document');
    } else if (activeType === 'Canvases') {
      items = items.filter((d) => d.kind === 'canvas');
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((d) =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.snippet || '').toLowerCase().includes(q)
      );
    }
    if (selectedTags.length > 0) {
      items = items.filter((d) => d.tags?.some((t) => selectedTags.includes(t)));
    }
    // Sort
    if (sortBy === 'alpha') {
      items = [...items].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else {
      items = [...items].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return items;
  }, [allDocuments, sharedDocs, accessFilter, activeType, searchQuery, selectedTags, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleArchive = useCallback((id: string) => {
    const doc = allDocuments.find((d) => d.id === id);
    service.archiveDoc(id, true);
    let instance: { destroy: () => void } | null = null;
    instance = notification.success({
      message: 'Moved to trash',
      description: doc?.title || 'Untitled',
      btn: (
        <Button
          size="sm"
          variant="text"
          className={styles.undoBtn}
          onClick={() => { service.archiveDoc(id, false); instance?.destroy(); }}
        >
          Undo
        </Button>
      ),
      duration: 5000,
    });
  }, [allDocuments, service]);

  const handleBulkArchive = useCallback(() => {
    selectedRowKeys.forEach((id) => {
      service.archiveDoc(id, true);
    });
    const count = selectedRowKeys.length;
    setSelectedRowKeys([]);
    notification.success({ message: `Moved ${count} item${count === 1 ? '' : 's'} to trash`, duration: 4000 });
  }, [selectedRowKeys, service]);

  const handleBulkAddToCollection = useCallback((collectionId: string) => {
    selectedRowKeys.forEach((id) => service.addToCollection(id, collectionId));
    setSelectedRowKeys([]);
  }, [selectedRowKeys, service]);

  const tableColumns = useMemo(() => [
    {
      title: 'Title',
      key: 'name',
      sorter: (a: WorkspaceDoc, b: WorkspaceDoc) => (a.title || '').localeCompare(b.title || ''),
      render: (_: unknown, doc: WorkspaceDoc) => (
        <DocTableTitleCell
          doc={doc}
          renaming={renamingId === doc.id}
          onOpen={navigateToEntity}
          onRename={(id, title) => { void service.renameDoc(id, title); }}
          onRenameEnd={() => setRenamingId(null)}
          isSelected={selectedRowKeys.includes(doc.id)}
          isSelecting={selectedRowKeys.length > 0}
          onToggleSelect={() => setSelectedRowKeys((prev) => prev.includes(doc.id) ? prev.filter((k) => k !== doc.id) : [...prev, doc.id])}
        />
      ),
    },
    {
      title: 'Owner',
      key: 'owner',
      width: 140,
      // Derive the owner from the ROW's access, not the logged-in user — a file
      // shared with me must show its real owner (access.grantedBy), not me.
      render: (_: unknown, doc: WorkspaceDoc) => {
        const owner = deriveOwner(doc.access, user);
        return (
          <Flex align="center" gap={space[2]}>
            {owner?.avatarUrl ? (
              <Avatar src={owner.avatarUrl} size="sm" />
            ) : (
              <Flex align="center" justify="center" className={tableStyles.avatarPlaceholder}>
                <Icon name="person" size={12} weight={iconWeight.light} />
              </Flex>
            )}
            {owner?.name && (
              <Text className={tableStyles.ownerName}>{owner.name.split(' ')[0]}</Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      width: 120,
      render: (_: unknown, doc: WorkspaceDoc) => {
        const pillClass = doc.kind === 'canvas'
          ? tableStyles.typePillCanvas
          : tableStyles.typePillDoc;
        return (
          <Tag bordered={false} className={pillClass}>
            {getEntityKindMeta(doc.kind).label}
          </Tag>
        );
      },
    },
    {
      title: 'Last Edited',
      key: 'updatedAt',
      width: 140,
      sorter: (a: WorkspaceDoc, b: WorkspaceDoc) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (_: unknown, doc: WorkspaceDoc) => (
        <Text className={tableStyles.dateText}>{formatDate(doc.updatedAt)}</Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_: unknown, doc: WorkspaceDoc) => (
        <Flex className={tableActionsCell} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={['click']}
            placement="bottom-end"
            menu={{
              items: [
                { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => navigateToEntity(doc.id) },
                { key: 'd1', label: '', type: 'divider' as const },
                ...(canEdit(doc.access) ? [{ key: 'rename', label: 'Rename', icon: <Icon name="edit" size={14} weight={iconWeight.light} />, onClick: () => setRenamingId(doc.id) }] : []),
                ...(canDelete(doc.access) ? [{ key: 'trash', label: 'Move to Trash', icon: <Icon name="delete" size={14} weight={iconWeight.light} />, danger: true, onClick: () => handleArchive(doc.id) }] : []),
                ...(canRemoveSelf(doc.access) ? [{
                  key: 'remove-self',
                  icon: <Icon name="logout" size={14} weight={iconWeight.light} />,
                  onClick: () => {},
                  label: (
                    <ConfirmMenuItem
                      label="Remove from my workspace"
                      confirmLabel="Click to confirm"
                      onConfirm={() => service.leaveEntity(doc.id).catch((err) => Sentry.captureException(err))}
                    />
                  ),
                }] : []),
              ],
            }}
          >
            <IconButton size="sm" aria-label="More options" className={tableStyles.moreBtn}>
              <Icon name="more_horiz" size={16} weight={iconWeight.light} />
            </IconButton>
          </Dropdown>
        </Flex>
      ),
    },
  ], [navigateToEntity, service, handleArchive, user, renamingId, selectedRowKeys, setSelectedRowKeys]);

  return (
    <>
    <Library
      title="Documents"
      heroIcon={getEntityKindMeta('document').icon}
      items={pagedItems}
      cardLayout={viewMode === 'grid' ? 'pageGrid' : undefined}
      viewMode={viewMode}
      onViewChange={setViewMode}
      accessFilter={accessFilter}
      onAccessFilterChange={handleAccessFilterChange}
      typeOptions={TYPE_OPTIONS}
      activeType={activeType}
      onTypeChange={(t) => { setActiveType(t); setPage(1); }}
      sortOptions={[
        { key: 'modified', label: 'Last Modified', icon: <Icon name="schedule" size={14} weight={iconWeight.light} /> },
        { key: 'alpha', label: 'Alphabetical', icon: <Icon name="sort_by_alpha" size={14} weight={iconWeight.light} /> },
      ]}
      activeSort={sortBy}
      onSortChange={setSortBy}
      tags={allTags}
      selectedTags={selectedTags}
      onTagsChange={setSelectedTags}
      tableColumns={tableColumns}
      onTableRow={(doc) => ({
        onClick: () => { if (renamingId !== doc.id) navigateToEntity(doc.id as string); },
      })}
      selectedIds={new Set(selectedRowKeys)}
      onToggleSelect={(id) => setSelectedRowKeys((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id])}
      onSelectAll={(ids) => setSelectedRowKeys(ids)}
      onClearSelection={() => setSelectedRowKeys([])}
      tableRowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
      selectionActions={
        selectedRowKeys.length > 0 ? (
          <SelectionActions
            actions={[
              {
                key: 'collections',
                icon: 'folder_open',
                label: 'Add to Collection',
                onClick: () => setCollectionPickerOpen((v) => !v),
                wrapper: (btn) => (
                  <>
                    {btn}
                    <BulkAddToCollectionPicker
                      open={collectionPickerOpen}
                      onClose={() => setCollectionPickerOpen(false)}
                      onAdd={handleBulkAddToCollection}
                    />
                  </>
                ),
              },
              {
                key: 'trash',
                icon: 'delete',
                label: 'Move to Trash',
                onClick: handleBulkArchive,
                danger: true,
              },
            ]}
          />
        ) : undefined
      }
      renderCard={(doc, _index, vm, checkboxNode) => (
        <DocumentCard
          doc={doc}
          listView={vm === 'list'}
          onOpen={(id) => navigateToEntity(id)}
          onArchive={handleArchive}
          checkboxNode={checkboxNode}
        />
      )}
      keyExtractor={(doc) => doc.id}
      searchPlaceholder="Search documents..."
      onSearch={handleSearch}
      emptyIllustration="documents"
      emptyTitle="No documents yet"
      emptyMessage="Your mind is a blank slate. Start capturing your thoughts and canvases."
      actions={
        <Dropdown
          trigger={['click']}
          placement="bottom-end"
          menu={{
            items: [
              {
                key: 'new-document',
                label: 'New Document',
                icon: <Icon name={getEntityKindMeta('document').icon} size={16} weight={iconWeight.light} />,
                extra: <Text size="xs" type="secondary">{shortcutLabel('⌘T', 'Ctrl+T')}</Text>,
                onClick: () => createNewDocument('document'),
              },
              {
                key: 'new-canvas',
                label: 'New Canvas',
                icon: <Icon name={getEntityKindMeta('canvas').icon} size={16} weight={iconWeight.light} />,
                extra: <Text size="xs" type="secondary">{shortcutLabel('⌘⇧T', 'Ctrl+Shift+T')}</Text>,
                onClick: () => createNewDocument('canvas'),
              },
            ],
          }}
        >
          <Button
            variant="primary"
            icon={<Icon name="add" size={18} weight={iconWeight.light} />}
            className={styles.primaryBtn}
          >
            New Document
          </Button>
        </Dropdown>
      }
      pagination={
        filteredItems.length > PAGE_SIZE ? (
          <Flex justify="center" className={styles.paginationRow}>
            <Pagination
              current={currentPage}
              total={filteredItems.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </Flex>
        ) : undefined
      }
    />
    </>
  );
}
